/* eslint-disable max-classes-per-file */
// eslint-disable-next-line no-restricted-syntax
import * as engagementPlugin from '@amplitude/engagement-browser';

import type { EventTrackingListener, TrackingEvent, TrackingEventProperties } from '@change-corgi/core/eventTracker';
import { getWindow } from '@change-corgi/core/window';

type AmplitudeEngagementEvent = Readonly<{
	event_type: string;
	event_properties: Record<string, unknown>;
}>;

type AmplitudeEngagement = Readonly<{
	forwardEvent: (event: AmplitudeEngagementEvent) => void;
	boot: (
		config: Readonly<{
			user: Readonly<{
				device_id?: string;
				user_properties: Record<string, unknown>;
			}>;
			integrations: ReadonlyArray<
				Readonly<{
					track: (event: AmplitudeEngagementEvent) => void;
				}>
			>;
		}>,
	) => Promise<void>;
}>;

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Window {
		engagement?: AmplitudeEngagement;
	}
}

abstract class AmplitudeGuidesAndSurveys implements EventTrackingListener {
	private enabled = false;
	protected loggingEnabled = false;
	private running = false;
	protected eventTracker?: {
		track: (eventType: string, eventProperties?: Record<string, unknown>) => Promise<boolean>;
	};

	setEventTracker(eventTracker: {
		track: (eventType: string, eventProperties?: Record<string, unknown>) => Promise<boolean>;
	}): this {
		this.eventTracker = eventTracker;
		return this;
	}

	setEnabled(enabled: boolean): this {
		this.enabled = enabled;
		this.onChange();
		return this;
	}

	setLoggingEnabled(loggingEnabled: boolean): this {
		this.loggingEnabled = loggingEnabled;
		return this;
	}

	protected log(message: string, ...args: unknown[]): void {
		if (this.loggingEnabled) {
			// eslint-disable-next-line no-console
			console.log(message, ...args);
		}
	}

	protected abstract start(): void;

	protected isEnabled(): boolean {
		if (!this.enabled) {
			return false;
		}
		return true;
	}

	private onChange(): void {
		if (this.isEnabled()) {
			if (!this.running) {
				this.start();
				this.running = true;
			}
			return;
		}
		if (this.running) {
			this.running = false;
		}
	}

	onEventsTrack(events: readonly TrackingEvent[], commonData: Readonly<TrackingEventProperties>): void {
		if (!this.isEnabled() || !this.running) {
			return;
		}

		events.forEach((event) => {
			this.forwardEvent(event, commonData);
		});
	}

	protected abstract forwardEvent(event: TrackingEvent, commonData: Readonly<TrackingEventProperties>): void;
}

class FakeAmplitudeGuidesAndSurveys extends AmplitudeGuidesAndSurveys {
	protected start(): void {
		this.log('[FAKE] Amplitude engagement starting');
	}

	protected forwardEvent(event: TrackingEvent, commonData: Readonly<TrackingEventProperties>): void {
		this.log('[FAKE] Forwarding event:', event.name, {
			event_type: event.name,
			event_properties: { ...commonData, ...event.data },
		});
	}
}

type RealAmplitudeGuidesAndSurveysOptions = {
	apiKey: string;
	deviceId?: string;
	locale?: string;
};

class RealAmplitudeGuidesAndSurveys extends AmplitudeGuidesAndSurveys {
	private options: RealAmplitudeGuidesAndSurveysOptions;

	constructor(options: RealAmplitudeGuidesAndSurveysOptions) {
		super();
		this.options = options;
	}

	protected start(): void {
		this.log('Amplitude engagement starting');
		engagementPlugin.init(this.options.apiKey, { locale: this.options.locale });

		const window = getWindow();
		if (window.engagement) {
			void window.engagement.boot({
				user: {
					device_id: this.options.deviceId, // Is the UUID
					user_properties: {},
				},
				integrations: [
					{
						track: (event) => {
							if (this.eventTracker) {
								void this.eventTracker.track(event.event_type, event.event_properties);
							}
						},
					},
				],
			});
		}
	}

	protected forwardEvent(event: TrackingEvent, commonData: Readonly<TrackingEventProperties>): void {
		this.log('Forwarding event to Amplitude:', event.name);
		const window = getWindow();
		window.engagement?.forwardEvent({
			event_type: event.name,
			event_properties: { ...commonData, ...event.data },
		});
	}
}

export type { AmplitudeGuidesAndSurveys };

export function createFakeAmplitudeGuidesAndSurveys(): AmplitudeGuidesAndSurveys {
	return new FakeAmplitudeGuidesAndSurveys();
}

export function createAmplitudeGuidesAndSurveys(
	options: RealAmplitudeGuidesAndSurveysOptions,
): AmplitudeGuidesAndSurveys {
	return new RealAmplitudeGuidesAndSurveys(options);
}
